/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useState } from "react";
import {
  Edit3,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, EmptyState, StatusPill, fmtDateTime } from "./_shared";

const SEVERITIES = [
  { value: "info", label: "Info", tone: "slate" },
  { value: "low", label: "Low", tone: "sky" },
  { value: "medium", label: "Medium", tone: "amber" },
  { value: "high", label: "High", tone: "rose" },
  { value: "critical", label: "Critical", tone: "rose" },
];

const STATUSES = [
  { value: "false_positive", label: "False positive", tone: "green" },
  { value: "accepted_risk", label: "Accepted risk", tone: "amber" },
  { value: "fix_pending", label: "Fix pending", tone: "sky" },
  { value: "fixed", label: "Fixed", tone: "slate" },
];

const blankForm = () => ({
  title: "",
  scanner: "",
  severity: "low",
  file_path: "",
  line_ref: "",
  status: "false_positive",
  justification: "",
});

export default function AdminCodeQuality() {
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/code-quality-notes");
      setNotes(data.notes || []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Failed to load code-quality notes.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm(blankForm());
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (note) => {
    setForm({
      title: note.title,
      scanner: note.scanner || "",
      severity: note.severity,
      file_path: note.file_path || "",
      line_ref: note.line_ref || "",
      status: note.status,
      justification: note.justification,
    });
    setEditingId(note.note_id);
    setShowForm(true);
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      if (editingId) {
        await api.patch(`/admin/code-quality-notes/${editingId}`, form);
        toast.success("Note updated");
      } else {
        await api.post("/admin/code-quality-notes", form);
        toast.success("Note added");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(blankForm());
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to save note.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (note) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete code-quality note "${note.title}"?`)) return;
    try {
      await api.delete(`/admin/code-quality-notes/${note.note_id}`);
      toast.success("Note deleted");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to delete note.",
      );
    }
  };

  return (
    <AdminLayout
      title="Code quality notes"
      subtitle="Persistent ledger for scanner findings — false positives, accepted risks, and pending fixes."
      actions={
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800"
          data-testid="cq-new-btn"
        >
          <Plus className="h-3.5 w-3.5" /> New note
        </button>
      }
    >
      <Card className="p-5 mb-5 bg-slate-50" testid="cq-help-card">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-700">
            <p>
              Record findings from external scanners (Snyk, npm audit, Semgrep,
              GitHub code-scanning, etc.) so triage decisions don't have to be
              re-explained every audit. Common false-positives we already track:
            </p>
            <ul className="list-disc ml-5 mt-2 space-y-1 text-slate-600">
              <li>
                React <code>dangerouslySetInnerHTML</code> — always preceded by{" "}
                <code>DOMPurify.sanitize</code>.
              </li>
              <li>
                Inline scripts in admin email templates — built server-side
                from a fixed allow-list.
              </li>
            </ul>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading notes…
        </div>
      ) : notes.length === 0 ? (
        <EmptyState
          title="No notes yet"
          body='Click "New note" above to record your first scanner finding.'
          testid="cq-empty"
        />
      ) : (
        <Card testid="cq-table-card" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Finding</th>
                  <th className="px-4 py-3">Severity</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Last reviewed</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {notes.map((n) => {
                  const sev = SEVERITIES.find((s) => s.value === n.severity);
                  const st = STATUSES.find((s) => s.value === n.status);
                  return (
                    <tr
                      key={n.note_id}
                      className="hover:bg-slate-50"
                      data-testid={`cq-row-${n.note_id}`}
                    >
                      <td className="px-4 py-3 max-w-md">
                        <div className="font-medium text-slate-900">
                          {n.title}
                        </div>
                        {n.scanner && (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Scanner: {n.scanner}
                          </div>
                        )}
                        <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                          {n.justification}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill
                          value={sev?.label || n.severity}
                          tone={sev?.tone}
                        />
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 font-mono">
                        {n.file_path || "—"}
                        {n.line_ref && (
                          <span className="block text-slate-400">
                            {n.line_ref}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill
                          value={st?.label || n.status}
                          tone={st?.tone}
                        />
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        <div>{fmtDateTime(n.last_reviewed_at)}</div>
                        <div className="text-slate-400">
                          by {n.last_reviewed_by}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(n)}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                            data-testid={`cq-edit-${n.note_id}`}
                          >
                            <Edit3 className="h-3.5 w-3.5" /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(n)}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
                            data-testid={`cq-delete-${n.note_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {showForm && (
        <NoteForm
          form={form}
          setForm={setForm}
          editing={!!editingId}
          onCancel={() => {
            setShowForm(false);
            setEditingId(null);
          }}
          onSubmit={onSubmit}
          submitting={submitting}
        />
      )}
    </AdminLayout>
  );
}

function NoteForm({ form, setForm, editing, onCancel, onSubmit, submitting }) {
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      data-testid="cq-form-modal"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-2xl bg-white rounded-2xl border border-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-slate-900">
              {editing ? "Edit note" : "New code-quality note"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Document the finding, where it came from and why it's classified
              the way it is.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Close"
            data-testid="cq-form-close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <span className="text-xs font-medium text-slate-700">
              Finding title
            </span>
            <input
              required
              type="text"
              value={form.title}
              onChange={set("title")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              placeholder="React dangerouslySetInnerHTML XSS warning"
              minLength={2}
              maxLength={200}
              data-testid="cq-form-title"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">
              Scanner / source
            </span>
            <input
              type="text"
              value={form.scanner}
              onChange={set("scanner")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              placeholder="Snyk / Semgrep / npm audit / manual review"
              maxLength={80}
              data-testid="cq-form-scanner"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">Severity</span>
            <select
              value={form.severity}
              onChange={set("severity")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="cq-form-severity"
            >
              {SEVERITIES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">
              File / path
            </span>
            <input
              type="text"
              value={form.file_path}
              onChange={set("file_path")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
              placeholder="src/components/RichTextEditor.jsx"
              maxLength={400}
              data-testid="cq-form-file"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">
              Line / reference
            </span>
            <input
              type="text"
              value={form.line_ref}
              onChange={set("line_ref")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
              placeholder="L42 or function name"
              maxLength={80}
              data-testid="cq-form-line"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-medium text-slate-700">Status</span>
            <select
              value={form.status}
              onChange={set("status")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="cq-form-status"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-medium text-slate-700">
              Justification / notes
            </span>
            <textarea
              required
              rows={5}
              value={form.justification}
              onChange={set("justification")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              placeholder="DOMPurify.sanitize is invoked before render at line 28. The injected HTML is bounded to the safelist defined in DOMPURIFY_CONFIG. Scanner cannot model this sanitiser, so this is a confirmed false-positive."
              minLength={2}
              maxLength={4000}
              data-testid="cq-form-justification"
            />
          </label>
        </div>
        <footer className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2 rounded-b-2xl">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
            data-testid="cq-form-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            data-testid="cq-form-submit"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            {editing ? "Save changes" : "Add note"}
          </button>
        </footer>
      </form>
    </div>
  );
}
